import { requestProvider } from './requestProvider'
import { useDelete, useGet, usePost, usePut } from './reactQuery'
import { QueryOptions } from 'src/generated-types/query-options'
import { queryClient } from 'src/query-client'
import { apiKeysQueryKeys } from 'src/query-keys'
import { useMutation } from 'react-query'

export interface ApiKeyListItemDto {
  id: number
  key: string
  name?: string | null
  createdAt: string
  lastUsedAt?: string | null
  revoked: boolean
  localityIds: number[]
  allLocalities: boolean
}

export interface CreateApiKeyRequest {
  name: string
  localityIds: number[]
  allLocalities: boolean
}

export interface SetApiKeyLocalitiesRequest {
  localityIds: number[]
  allLocalities: boolean
}

export interface CreateApiKeyResponse {
  id: number
  key: string
  secret: string
}

export interface RotateApiKeyResponse {
  key: string
  secret: string
}

const baseUrl = (customerId: EntityId) => `customers/${customerId}/api-keys`

export const apiKeysEndpoints = {
  list(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  create(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  rotate(customerId: EntityId, id: number) {
    return `${baseUrl(customerId)}/${id}/rotate`
  },
  revoke(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  localities(customerId: EntityId, id: number) {
    return `${baseUrl(customerId)}/${id}/localities`
  },
}

export const invalidateApiKeysCache = {
  list(customerId: EntityId) {
    queryClient.invalidateQueries(apiKeysQueryKeys.GET_API_KEYS(customerId))
  },
}

export const useGetApiKeys = (customerId: EntityId, options?: QueryOptions) =>
  useGet<ApiKeyListItemDto[]>(apiKeysEndpoints.list(customerId), undefined, {
    queryKey: apiKeysQueryKeys.GET_API_KEYS(customerId),
    ...options,
  })

export const useCreateApiKey = (customerId: EntityId) =>
  usePost<CreateApiKeyResponse, CreateApiKeyRequest>(apiKeysEndpoints.create(customerId))

export const useSetApiKeyLocalities = (customerId: EntityId, id: number) =>
  usePut<ApiKeyListItemDto, SetApiKeyLocalitiesRequest>(apiKeysEndpoints.localities(customerId, id))

export const useRotateApiKey = (customerId: EntityId) =>
  useMutation<RotateApiKeyResponse, Error, number>((id: number) =>
    requestProvider.post<RotateApiKeyResponse>(apiKeysEndpoints.rotate(customerId, id), {}).then(res => res.data),
  )

export const useRevokeApiKey = (customerId: EntityId) =>
  useDelete<ApiKeyListItemDto[]>(apiKeysEndpoints.revoke(customerId))
